import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { CreateListResult } from '@xemplo/gp-api';
import { USER_QUERY_KEYS, UserEndpoints } from '@xemplo/gp-constants';
import {
  CreateUserType,
  NewUserFormInput,
  PersonaRole,
  UpdateUserFormInput,
  UserPostApiActions,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { useUsersUrls } from './use-users-urls';
import { EnableDisableUserProps } from './users-query.types';

function getEnableDisableEndpoint(
  roleCode: string | undefined,
  isPayrollManager?: boolean
) {
  switch (roleCode) {
    case PersonaRole.PSPAdmin:
    case PersonaRole.SystemAdmin:
      return isPayrollManager
        ? UserEndpoints.enableDisablePSPAServiceProviderUser
        : UserEndpoints.enableDisablePSPACompanyUser;
    default:
      return UserEndpoints.enableDisableCompanyUser;
  }
}
export const useEnableDisableUser = (props: EnableDisableUserProps) => {
  const { id, onError, onSuccess, isPayrollManager } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const { data: authData } = useRoleAuthorisation();
  const endpoint = getEnableDisableEndpoint(authData?.result.roleName, isPayrollManager);
  const url = apiUrlBuilder(endpoint(id ?? ''), applicationApiUrl);

  return useMutation({
    mutationFn: () => client.patch<CreateListResult>(url).then((resp) => resp.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEYS.GET_USER_LIST],
      });
      onSuccess?.(data, variables, context);
    },
    onError,
  });
};

/**These types are a simplified version of the generic types provided by react-query.
 * This query is only really used in one place and we don't have a need for the extenstive typed parameters */
export interface EnableDisableMultiFactorProps {
  onSuccess?: (data: unknown, variables: unknown, context: unknown) => unknown;
  onError?: (error: AxiosError<unknown>, context: unknown) => unknown;
  id: string | null;
}
export interface MfaPayload {
  deviceEnrolmentEnable: number;
  mfaEnable: boolean;
  requestType: string;
}

export const useEnableDisableMultiFactor = (props: EnableDisableMultiFactorProps) => {
  const { id, onError, onSuccess } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const url = apiUrlBuilder(
    UserEndpoints.enableDisableMultiFactor(id ?? ''),
    applicationApiUrl
  );

  return useMutation({
    mutationFn: (payload: MfaPayload) =>
      client.post<CreateListResult>(url, payload).then((resp) => resp.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEYS.GET_USER_LIST],
      });
      onSuccess?.(data, variables, context);
    },
    onError,
  });
};

export function useCreateUser(userType: CreateUserType) {
  const { getCreateUserUrl } = useUsersUrls();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = getCreateUserUrl(userType);
  return useMutation({
    mutationFn: (data: NewUserFormInput) => client.post(url, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEYS.GET_USER_LIST] }),
  });
}

export function useEditUser() {
  const { postUserActionUrl } = useUsersUrls();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      args: { data: UpdateUserFormInput } & Omit<PerformUserAction, 'action'>
    ) => {
      const url = postUserActionUrl(
        args.activeUserRole,
        args.usersType,
        args.userId,
        UserPostApiActions.Edit
      );

      return client.put(url, args.data);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEYS.GET_USER_LIST] }),
  });
}

export type usePerformUserActionProps = {
  onSuccess?: (data: unknown, variables: PerformUserAction, context: unknown) => unknown;
  onError?: (
    error: AxiosError<unknown>,
    variables: PerformUserAction,
    context: unknown
  ) => unknown;
};

export interface PerformUserAction {
  activeUserRole: PersonaRole;
  userId: string;
  usersType: CreateUserType;
  action: UserPostApiActions;
}

export function usePerformUserAction(props: usePerformUserActionProps) {
  const { postUserActionUrl } = useUsersUrls();
  const { client } = useHttpClient();
  const { onError, onSuccess } = props;

  return useMutation({
    mutationFn: async (payload) => {
      const { activeUserRole, usersType, userId, action } = payload;
      const url = postUserActionUrl(activeUserRole, usersType, userId, action);
      return client.post(url).then((resp) => resp.data);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    onError,
  });
}
